/** @jsxImportSource @emotion/react */

import React from "react";

import * as DashboardUtils from "js/dashboards/utils";

import TextField from "js/common/views/inputs/text-field";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {css} from "@emotion/react";
import * as Users from "js/common/users";
import {getUserOrCurrent} from "js/common/users";
import Tooltip from "js/common/views/tooltips";
import Immutable from "immutable";
import {PreviewTooltipContent} from "js/common/views/filter-drawer/filter-button";
import {getGroupOrCurrent} from "js/common/groups";
import {capitaliseWords} from "js/common/utils/strings";
import {Button, Menu, MenuItem} from "@mui/material";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import ResizeObserver from "resize-observer-polyfill";
import {indexBy} from "js/common/utils/collections";
import * as SavedConfigs from "js/common/saved-configs";
import * as Ajax from "js/common/ajax";
import * as tagRepo from "js/common/repo/backbone/tag-repo";

export const BasicComponentTitleBar = React.memo(
    ({
      config,
      componentId,
      titleIsEditable,
      title,
      onTitleChange,
      onEditClick,
      onRemoveClick,
      onDownloadClickRef,
      canDownload,
      isClickThrough,
      canFullScreen,
      isFullScreen,
      onFullScreenClick,
      onDuplicateClick,
      isEditing,
      inheritKeys,
      variant
    }) => {
      const {isFullEditor} = DashboardUtils.useEditorFlags();
      const {theme} = React.useContext(CustomThemeContext);
      const [anchorEl, setAnchorEl] = React.useState(null);
      const [filterNames, setFilterNames] = React.useState(Immutable.Map());
      const [filtersTextIsVisible, setFiltersTextIsVisible] = React.useState(true);
      const open = Boolean(anchorEl);
      const parentRef = React.useRef(null);

      React.useEffect(() => {
        if (!config) {
          return;
        }
        const filteredClients = config.getIn(["clientFilter", "clientIds"], Immutable.List());
        const filteredClientSets = config.getIn(["clientFilter", "clientSetIds"], Immutable.List());

        const loadFilterData = async () => {
          const loadIdToClientSet = (clientSetIds) => {
            if (clientSetIds.size === 0) {
              return Promise.resolve(Immutable.Map());
            }

            const suffixWithNameOwnershipTypes = ["sharedUserConfigs", "sharedGroupConfigs"];
            return SavedConfigs
                .getAll("CLIENT_SET")
                .then(ownershipTypeToSavedClientSets => {
                  const ownedIds = ownershipTypeToSavedClientSets.get("ownedConfigs").map(config => config.get("id")).toSet();
                  const clientSets = ownershipTypeToSavedClientSets
                      .update("ownedConfigs", configs => configs.map(toClientSet))
                      .update("sharedUserConfigs", configs => configs
                          .filter(config => !ownedIds.has(config.get("id")))
                          .map(toClientSet))
                      .update("sharedGroupConfigs", configs => configs
                          .filter(config => !ownedIds.has(config.get("id")))
                          .map(toClientSet))
                      .map((clientSets, ownershipType) => clientSets
                          .map(clientSet => clientSetToItem(clientSet, suffixWithNameOwnershipTypes.includes(ownershipType))))
                      .toList()
                      .flatMap(sets => sets);
                  const idToClientSet = indexBy(set => set.get("id"), clientSets);
                  const clientSetIdsNotVisible = clientSetIds.toSet().subtract(idToClientSet.keySeq().toSet());
                  const idToNamePlaceholder = indexBy(x => x, clientSetIdsNotVisible).map(id => {
                    return Immutable.Map({
                      clientSetId: "CLIENT_SET_" + id,
                      name: "[this client set has not been shared with you]",
                      type: "CLIENT_SET",
                      id,
                      clientCount: 0
                    });
                  });
                  return idToClientSet.merge(idToNamePlaceholder);
                });
          };

          const loadIdToClient = (clientIds) => {
            if (clientIds.size === 0) {
              return Promise.resolve(Immutable.Map());
            }

            return Ajax
                .post({url: "entity-finder", json: {entityType: "CLIENT", ids: clientIds.toJS()}})
                .then(x => indexBy(c => c.get("id"), Immutable.fromJS(x).map(clientToItem)));
          };

          const promises = [];

          // Load client sets
          promises.push(loadIdToClientSet(filteredClientSets));

          // Load clients
          if (filteredClients.size > 0) {
            promises.push(loadIdToClient(filteredClients));
          } else {
            promises.push(Promise.resolve(Immutable.Map()));
          }

          // Load tags
          promises.push(Promise.resolve(indexBy(
              tag => tag.get("id"),
              getTagOptions()
          )));

          try {
            const [idToClientSet, idToClient, idToTag] = await Promise.all(promises);
            setFilterNames(Immutable.Map({
              "CLIENTS": idToClient,
              "CLIENT_SETS": idToClientSet,
              "TAGS": idToTag
            }));
          } catch (error) {
            console.error("Error loading filter data:", error);
          }
        };

        void loadFilterData();
      }, [config, inheritKeys]);

      React.useLayoutEffect(() => {
        const parentElement = parentRef.current;
        if (!parentElement) {
          return;
        }

        const updateVisibility = () => {
          const width = parentElement.offsetWidth;
          setFiltersTextIsVisible(width >= 400);
        };

        updateVisibility();
        const resizeObserver = new ResizeObserver(() => {
          updateVisibility();
        });

        resizeObserver.observe(parentElement);

        return () => {
          resizeObserver.disconnect();
        };
      }, [config]);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };
      const handleClose = () => {
        setAnchorEl(null);
      };

      const hasFilterOverrides = inheritKeys && DashboardUtils.defaultInheritKeys.count() !== inheritKeys.count();
      const isEditingHeader = !title && !titleIsEditable;
      const showKebabMenu = (isEditing || (canFullScreen && !isClickThrough) || (canDownload && !isClickThrough));

      const tooltipContent = React.useMemo(
          () => getTooltipContent(config, title, inheritKeys, hasFilterOverrides, filterNames),
          [config, title, inheritKeys, hasFilterOverrides, filterNames]);

      const getTagOptions = () => {
        return Immutable
            .fromJS(tagRepo.getAll().toJSON())
            .map(tag => {
              const category = tagRepo.getCategory(tag.get("categoryId"));
              return Immutable.Map({
                category: category.get("name"),
                id: tag.get("id"),
                name: tag.get("name")
              });
            });
      };

      const clientToItem = (client) => {
        return new Immutable.Map({
          id: client.get("id"),
          name: client.get("originalCrmId") + " - " + client.get("name"),
          type: "CLIENT"
        });
      };

      const clientSetToItem = (clientSet, suffixWithName) => {
        const owner = Users.getUser(clientSet.get("ownerId"));
        let name;
        if (suffixWithName && owner) {
          name = clientSet.get("name") + " - " + owner.get("firstName");
        } else {
          name = clientSet.get("name");
        }
        return new Immutable.Map({
          clientSetId: "CLIENT_SET_" + clientSet.get("id"),
          name,
          type: "CLIENT_SET",
          id: clientSet.get("id"),
          clientCount: clientSet.get("json").get("clientIds").size
        });
      };

      const toClientSet = (config) => {
        return config.updateIn(["json", "clientIds"], clientIds => clientIds.toSet());
      };

      return (
          <div
              ref={parentRef}
              style={{
                backgroundColor: "transparent",
                display: "flex",
                width: showKebabMenu ? `calc(100% - ${isFullScreen ? "55px" : "14px"})` : "100%",
                justifyContent: "space-between",
                height: 27,
                position: "absolute",
                borderTopRightRadius: 5,
                padding: 10,
                marginTop: isEditingHeader ? -4 : 2,
                borderTopLeftRadius: 5,
                zIndex: 2
              }}
          >
            <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%"
                }}
            >
              {isEditing && <ComponentDragHandle variant={variant} isEditing={isEditing} />}

              <div style={{maxWidth: "100%", width: isEditing ? "100%" : "auto"}}>
                <Tooltip
                    tooltipHtml={() => tooltipContent}
                    position={hasFilterOverrides ? "topRightOffset" : "top"}
                    positionOffset={filtersTextIsVisible ? 50 : 13}
                    styleType={theme.themeId}
                    maxWidth={600}
                    width="max-content"
                    wrap={true}
                    disable={isEditing && !hasFilterOverrides}
                >
                  <div style={{display: "flex", gap: "5px", alignItems: "center"}}>
                    {isEditing && titleIsEditable ? (
                        <TextField
                            placeholder="Optional Title"
                            data-test-id="title-input"
                            value={title}
                            onChange={(e) => onTitleChange(componentId, e.target.value)}
                            style={{width: "100%", fontSize: 12, fontWeight: 500}}
                        />
                    ) : (
                        title && <span
                            data-test-id="grid-title"
                            style={{
                              fontWeight: 500,
                              fontSize: 12,
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              display: "block"
                            }}>
                {title}
              </span>
                    )}
                    {hasFilterOverrides && <FilterOverridesChip filtersTextIsVisible={filtersTextIsVisible} />}
                  </div>
                </Tooltip>
              </div>

              {showKebabMenu && (
                  <>
                    <Button
                        className="hide-for-print"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        style={{
                          background: open
                              ? variant === "transparent"
                                  ? theme.palette.background.card
                                  : theme.palette.background.paper
                              : "transparent",
                          padding: 0,
                          marginRight: isEditingHeader ? "0px" : "-15px",
                          height: 20,
                          boxShadow: "none",
                          borderRadius: 5,
                          flexShrink: 0,
                          minWidth: 0
                        }}
                        data-test-id={`${componentId}-menu`}
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                    >
                      <i className="bhi-kebab-menu" style={{fontSize: 14}} />
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        transitionDuration={0}
                        onClose={handleClose}
                        MenuListProps={{
                          style: {background: theme.palette.background.card}
                        }}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right"
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right"
                        }}
                    >
                      {isFullEditor && isEditing && (
                          <MenuItem
                              onClick={() => {
                                onEditClick({componentId});
                                handleClose();
                              }}
                          >
                            <ComponentEditButton componentId={componentId} isEditing={isEditing} />
                          </MenuItem>
                      )}
                      {isFullEditor && isEditing && (
                          <MenuItem
                              onClick={() => {
                                onRemoveClick(componentId);
                                handleClose();
                              }}
                          >
                            <ComponentDeleteButton componentId={componentId} isEditing={isEditing} />
                          </MenuItem>
                      )}
                      {canFullScreen && !isClickThrough && (
                          <MenuItem
                              onClick={() => {
                                onFullScreenClick(componentId, !isFullScreen);
                                handleClose();
                              }}
                          >
                            <FullScreenButton componentId={componentId} isFullScreen={isFullScreen} />
                          </MenuItem>
                      )}
                      {isFullEditor && isEditing && (
                          <MenuItem
                              onClick={() => {
                                onDuplicateClick(componentId);
                                handleClose();
                              }}
                          >
                            <DuplicateButton />
                          </MenuItem>
                      )}
                      {canDownload && !isClickThrough && (
                          <MenuItem
                              onClick={() =>
                                  Users.currentHasPermission("EXPORT_FILE")
                                      ? onDownloadClickRef.current().enabled && onDownloadClickRef.current().downloadFn()
                                      : null
                              }
                          >
                            <DownloadButton downloadFn={onDownloadClickRef.current} />
                          </MenuItem>
                      )}
                    </Menu>
                  </>
              )}
            </div>
          </div>
      );
    }
);

const getTooltipContent = (config, title, inheritKeys, hasFilterOverrides, filterNames) => {
  if (!config) {
    return title ? <div style={{fontWeight: 600, fontSize: 14}}>{title}</div> : null;
  }

  const customKeys = hasFilterOverrides
      ? DashboardUtils.defaultInheritKeys.toJS().filter((x) => !inheritKeys.toJS().includes(x))
      : [];

  const excludedTags = config.getIn(["tagFilter", "excludedTagIds"], Immutable.List());
  const matchAllTags = config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List());
  const matchAnyTags = config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List());
  const filteredClients = config.getIn(["clientFilter", "clientIds"], Immutable.List());
  const filteredClientSets = config.getIn(["clientFilter", "clientSetIds"], Immutable.List());
  const timeframe = TimeframeRepo.parse(config.get("timeframe").toJS());

  return <div style={{textAlign: "left"}}>
    {title &&
        <div style={{
          fontWeight: 600,
          fontSize: 14,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>{title}</div>
    }
    {hasFilterOverrides && (
        <div>
          {title && <hr />}
          {customKeys.includes("timeframe") && (
              <div style={{marginBottom: 5}}>
                <i style={{fontSize: 14, marginRight: 5, position: "relative", bottom: 1}} className="bhi-calendar" />
                <span style={{fontWeight: 600}}>Date Range: </span>
                {timeframe.get("start").format("YYYY-MM-DD") + " to " + timeframe.get("end").format("YYYY-MM-DD")}
              </div>
          )}
          {customKeys.includes("qualifier") && (
              <div>
                <i style={{fontSize: 14, marginRight: 5, position: "relative", bottom: 1}} className="bhi-users" />
                <span style={{fontWeight: 600}}>{capitaliseWords(config.getIn(["qualifier", "type"]))}: </span>
                {config.getIn(["qualifier", "type"]) === "GROUP"
                    ? getGroupOrCurrent(config.getIn(["qualifier", "id"])).attributes.name
                    : getUserOrCurrent(config.getIn(["qualifier", "id"])).attributes.fullName}
              </div>
          )}
          <PreviewTooltipContent
              hasShowAll={true}
              showClientFilters={customKeys.includes("clientFilter")}
              showTagFilters={customKeys.includes("tagFilter")}
              filterNames={filterNames}
              matchAllTagIds={matchAllTags}
              matchAnyTagIds={matchAnyTags}
              excludedTagIds={excludedTags}
              clientIds={filteredClients}
              clientSetIds={filteredClientSets}
          />
        </div>
    )}
  </div>;
};

const FilterOverridesChip = React.memo(({filtersTextIsVisible}) => {
  const {theme} = React.useContext(CustomThemeContext);

  return (
      <div
          style={{
            display: "flex",
            borderRadius: "5px",
            alignItems: "center",
            padding: "2px 5px",
            backgroundColor: theme.palette.background.chip,
            cursor: "help"
          }}
      >
        <i
            style={{
              color: theme.palette.button.primary,
              textAlign: "center",
              paddingRight: "2px",
              fontSize: 12
            }}
            className="bhi-filter"
        />
        {filtersTextIsVisible && (
            <div style={{color: theme.palette.button.primary, fontSize: 12, textWrap: "nowrap"}}>Filters Applied</div>
        )}
      </div>
  );
});

const menuItemStyle = {
  display: "flex",
  fontSize: 12,
  alignItems: "center"
};

const IconStyle = (hoverColor, suppressMargin = false) => css`
  cursor: pointer;
  font-size: 15px;
  margin-right: ${suppressMargin ? 0 : 10}px;
  &:hover {
    color: ${hoverColor} !important;
    opacity: 1;
  };
`;

const ComponentEditButton = React.memo(({componentId}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
      <div style={menuItemStyle}>
        <i css={IconStyle(theme.palette.primary.main)} data-test-id={`${componentId}-edit-button`} className="bhi-edit" />
        Edit
      </div>
  );
});

const ComponentDragHandle = React.memo(({variant}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
      <i
          css={IconStyle(theme.palette.primary.main)}
          style={{marginLeft: -4, marginRight: 5, marginTop: variant === "transparent" ? -8 : 3}}
          className="cube19-drag-handle bhi-move"
          data-test-id="drag-handle"
      />
  );
});

const ComponentDeleteButton = React.memo(({componentId}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
      <div style={menuItemStyle}>
        <i
            css={IconStyle(theme.palette.error.main)}
            data-test-id={`${componentId}-delete-button`}
            className="bhi-delete"
        />
        Delete
      </div>
  );
});

const FullScreenButton = React.memo(({isFullScreen, componentId}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
      <div style={menuItemStyle}>
        <i
            css={IconStyle(theme.palette.primary.main)}
            data-test-id={`${componentId}-fullscreen-button`}
            className={isFullScreen ? "fas fa-compress" : "bhi-card-expand"}
        />
        {isFullScreen ? "Collapse" : "Expand"}
      </div>
  );
});

const DuplicateButton = React.memo(() => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
      <div style={menuItemStyle}>
        <i css={IconStyle(theme.palette.primary.main)} className="bhi-duplicate" />
        Duplicate
      </div>
  );
});

export const DownloadButton = React.memo(({downloadFn}) => {
  const {theme} = React.useContext(CustomThemeContext);
  if (typeof downloadFn !== "function") {
    return null;
  }
  const {enabled, tooltip} = downloadFn();

  return (
      <div
          style={{
            ...menuItemStyle,
            opacity: enabled ? 1 : 0.4,
            cursor: enabled ? "pointer" : "not-allowed"
          }}
          title={tooltip}
      >
        <i
            css={IconStyle(theme.palette.text.main)}
            className={"bhi-download"}
            style={{cursor: enabled ? "pointer" : "not-allowed"}}
        />
        Download
      </div>
  );
});